/**
 * Adapted from PlayCanvas Engine's Unified GSplat container format chunks at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 *
 * The frozen upstream compact comments say 20 bytes/splat, but the declared bindings occupy
 * 4 + 16 + 4 = 24 bytes/splat. This port intentionally records the real layout.
 */

export const GSPLAT_COMPACT_READ_GLSL = /* glsl */ `
// Compact work buffer format (24 bytes/splat).
// Required call order: getCenter(), getOpacity(), then getColor().
uvec4 cachedTransformA;

vec3 getCenter() {
    cachedTransformA = loadDataTransformA();
    return vec3(uintBitsToFloat(cachedTransformA.r), uintBitsToFloat(cachedTransformA.g), uintBitsToFloat(cachedTransformA.b));
}

float getOpacity() {
    return float(cachedTransformA.a >> 24u) / 255.0;
}

vec3 getColor() {
    uint data = loadDataColor().x;
    float r = float(data & 0x7FFu) * (4.0 / 2047.0);
    float g = float((data >> 11u) & 0x7FFu) * (4.0 / 2047.0);
    float b = float((data >> 22u) & 0x3FFu) * (4.0 / 1023.0);
    return vec3(r, g, b);
}

vec4 getRotation() {
    uint data = loadDataTransformB().x;
    vec3 p = vec3(
        float(data & 0x7FFu) / 2047.0 * 2.0 - 1.0,
        float((data >> 11u) & 0x7FFu) / 2047.0 * 2.0 - 1.0,
        float((data >> 22u) & 0x3FFu) / 1023.0 * 2.0 - 1.0
    );
    float d = dot(p, p);
    return vec4(1.0 - d, sqrt(max(0.0, 2.0 - d)) * p);
}

vec3 getScale() {
    uint data = cachedTransformA.a;
    float sx = float(data & 0xFFu);
    float sy = float((data >> 8u) & 0xFFu);
    float sz = float((data >> 16u) & 0xFFu);
    const float logRange = 21.0 / 255.0;
    const float logMin = -12.0;
    return vec3(
        sx == 0.0 ? 0.0 : exp(sx * logRange + logMin),
        sy == 0.0 ? 0.0 : exp(sy * logRange + logMin),
        sz == 0.0 ? 0.0 : exp(sz * logRange + logMin)
    );
}
`

export const GSPLAT_COMPACT_READ_WGSL = /* wgsl */ `
// Compact work buffer format (24 bytes/splat).
// Required call order: getCenter(), getOpacity(), then getColor().
var<private> cachedTransformA: vec4u;

fn getCenter() -> vec3f {
    cachedTransformA = loadDataTransformA();
    return vec3f(bitcast<f32>(cachedTransformA.r), bitcast<f32>(cachedTransformA.g), bitcast<f32>(cachedTransformA.b));
}

fn getOpacity() -> f32 {
    return f32(cachedTransformA.a >> 24u) / 255.0;
}

fn getColor() -> vec3f {
    let data = loadDataColor().x;
    let r = f32(data & 0x7FFu) * (4.0 / 2047.0);
    let g = f32((data >> 11u) & 0x7FFu) * (4.0 / 2047.0);
    let b = f32((data >> 22u) & 0x3FFu) * (4.0 / 1023.0);
    return vec3f(r, g, b);
}

fn getRotation() -> vec4f {
    let data = loadDataTransformB().x;
    let p = vec3f(
        f32(data & 0x7FFu) / 2047.0 * 2.0 - 1.0,
        f32((data >> 11u) & 0x7FFu) / 2047.0 * 2.0 - 1.0,
        f32((data >> 22u) & 0x3FFu) / 1023.0 * 2.0 - 1.0
    );
    let d = dot(p, p);
    return vec4f(1.0 - d, sqrt(max(0.0, 2.0 - d)) * p);
}

fn getScale() -> vec3f {
    let data = cachedTransformA.a;
    let sx = f32(data & 0xFFu);
    let sy = f32((data >> 8u) & 0xFFu);
    let sz = f32((data >> 16u) & 0xFFu);
    let logRange = 21.0 / 255.0;
    let logMin = -12.0;
    return vec3f(
        select(exp(sx * logRange + logMin), 0.0, sx == 0.0),
        select(exp(sy * logRange + logMin), 0.0, sy == 0.0),
        select(exp(sz * logRange + logMin), 0.0, sz == 0.0)
    );
}
`

export const GSPLAT_COMPACT_WRITE_GLSL = /* glsl */ `
// Compact work buffer format (24 bytes/splat).
void writeSplat(vec3 center, vec4 rotation, vec3 scale, vec4 color) {
    vec3 rgb = clamp(color.rgb, 0.0, 4.0);
    uint rBits = uint(rgb.r * (2047.0 / 4.0) + 0.5);
    uint gBits = uint(rgb.g * (2047.0 / 4.0) + 0.5);
    uint bBits = uint(rgb.b * (1023.0 / 4.0) + 0.5);
    writeDataColor(uvec4(rBits | (gBits << 11u) | (bBits << 22u), 0u, 0u, 0u));

    #ifndef GSPLAT_COLOR_ONLY
        vec4 q = rotation;
        if (q.w < 0.0) q = -q;
        vec3 p = q.xyz * inversesqrt(1.0 + q.w);
        uint aBitsQ = uint(clamp((p.x * 0.5 + 0.5) * 2047.0 + 0.5, 0.0, 2047.0));
        uint bBitsQ = uint(clamp((p.y * 0.5 + 0.5) * 2047.0 + 0.5, 0.0, 2047.0));
        uint cBitsQ = uint(clamp((p.z * 0.5 + 0.5) * 1023.0 + 0.5, 0.0, 1023.0));
        uint packedQuat = aBitsQ | (bBitsQ << 11u) | (cBitsQ << 22u);

        const float invLogRange = 255.0 / 21.0;
        const float logMin = -12.0;
        uint sxBits = scale.x < 1e-10 ? 0u : uint(clamp((log(scale.x) - logMin) * invLogRange + 0.5, 1.0, 255.0));
        uint syBits = scale.y < 1e-10 ? 0u : uint(clamp((log(scale.y) - logMin) * invLogRange + 0.5, 1.0, 255.0));
        uint szBits = scale.z < 1e-10 ? 0u : uint(clamp((log(scale.z) - logMin) * invLogRange + 0.5, 1.0, 255.0));
        uint alphaBits = uint(clamp(color.a, 0.0, 1.0) * 255.0 + 0.5);
        uint packedScaleAlpha = sxBits | (syBits << 8u) | (szBits << 16u) | (alphaBits << 24u);

        writeDataTransformA(uvec4(floatBitsToUint(center.x), floatBitsToUint(center.y), floatBitsToUint(center.z), packedScaleAlpha));
        writeDataTransformB(uvec4(packedQuat, 0u, 0u, 0u));
    #endif
}
`

export const GSPLAT_COMPACT_WRITE_WGSL = /* wgsl */ `
// Compact work buffer format (24 bytes/splat).
fn writeSplat(center: vec3f, rotation: vec4f, scale: vec3f, color: vec4f) {
    let rgb = clamp(color.rgb, vec3f(0.0), vec3f(4.0));
    let rBits = u32(rgb.r * (2047.0 / 4.0) + 0.5);
    let gBits = u32(rgb.g * (2047.0 / 4.0) + 0.5);
    let bBits = u32(rgb.b * (1023.0 / 4.0) + 0.5);
    writeDataColor(vec4u(rBits | (gBits << 11u) | (bBits << 22u), 0u, 0u, 0u));

    #ifndef GSPLAT_COLOR_ONLY
        var q = rotation;
        if (q.w < 0.0) { q = -q; }
        let p = q.xyz * inverseSqrt(1.0 + q.w);
        let aBitsQ = u32(clamp(p.x * 0.5 + 0.5, 0.0, 1.0) * 2047.0 + 0.5);
        let bBitsQ = u32(clamp(p.y * 0.5 + 0.5, 0.0, 1.0) * 2047.0 + 0.5);
        let cBitsQ = u32(clamp(p.z * 0.5 + 0.5, 0.0, 1.0) * 1023.0 + 0.5);
        let packedQuat = aBitsQ | (bBitsQ << 11u) | (cBitsQ << 22u);

        let invLogRange = 255.0 / 21.0;
        let logMin = -12.0;
        let sxBits = select(u32(clamp((log(scale.x) - logMin) * invLogRange + 0.5, 1.0, 255.0)), 0u, scale.x < 1e-10);
        let syBits = select(u32(clamp((log(scale.y) - logMin) * invLogRange + 0.5, 1.0, 255.0)), 0u, scale.y < 1e-10);
        let szBits = select(u32(clamp((log(scale.z) - logMin) * invLogRange + 0.5, 1.0, 255.0)), 0u, scale.z < 1e-10);
        let alphaBits = u32(clamp(color.a, 0.0, 1.0) * 255.0 + 0.5);
        let packedScaleAlpha = sxBits | (syBits << 8u) | (szBits << 16u) | (alphaBits << 24u);

        writeDataTransformA(vec4u(bitcast<u32>(center.x), bitcast<u32>(center.y), bitcast<u32>(center.z), packedScaleAlpha));
        writeDataTransformB(vec4u(packedQuat, 0u, 0u, 0u));
    #endif
}
`

export const GSPLAT_LARGE_READ_GLSL = /* glsl */ `
// Large work buffer format (32 bytes/splat).
uvec4 cachedTransformA;
uvec2 cachedTransformB;
vec4 cachedColor;

vec3 getCenter() {
    cachedTransformA = loadDataTransformA();
    cachedTransformB = loadDataTransformB().xy;
    return vec3(uintBitsToFloat(cachedTransformA.r), uintBitsToFloat(cachedTransformA.g), uintBitsToFloat(cachedTransformA.b));
}

float getOpacity() {
    #ifdef GSPLAT_COLOR_FLOAT
        cachedColor = loadDataColor();
    #else
        uvec4 packedColor = loadDataColor();
        uint packed_rg = packedColor.r | (packedColor.g << 16u);
        uint packed_ba = packedColor.b | (packedColor.a << 16u);
        cachedColor = vec4(unpackHalf2x16(packed_rg), unpackHalf2x16(packed_ba));
    #endif
    return cachedColor.a;
}

vec3 getColor() {
    return cachedColor.rgb;
}

vec4 getRotation() {
    vec2 rotXY = unpackHalf2x16(cachedTransformA.a);
    vec2 rotZscaleX = unpackHalf2x16(cachedTransformB.x);
    vec3 rotXYZ = vec3(rotXY, rotZscaleX.x);
    return vec4(rotXYZ, sqrt(max(0.0, 1.0 - dot(rotXYZ, rotXYZ)))).wxyz;
}

vec3 getScale() {
    vec2 rotZscaleX = unpackHalf2x16(cachedTransformB.x);
    vec2 scaleYZ = unpackHalf2x16(cachedTransformB.y);
    return vec3(rotZscaleX.y, scaleYZ);
}
`

export const GSPLAT_LARGE_READ_WGSL = /* wgsl */ `
// Large work buffer format (32 bytes/splat).
var<private> cachedTransformA: vec4u;
var<private> cachedTransformB: vec2u;
var<private> cachedColor: vec4f;

fn getCenter() -> vec3f {
    cachedTransformA = loadDataTransformA();
    cachedTransformB = loadDataTransformB().xy;
    return vec3f(bitcast<f32>(cachedTransformA.r), bitcast<f32>(cachedTransformA.g), bitcast<f32>(cachedTransformA.b));
}

fn getOpacity() -> f32 {
    #ifdef GSPLAT_COLOR_FLOAT
        cachedColor = loadDataColor();
    #else
        let packedColor = loadDataColor();
        let packed_rg = packedColor.r | (packedColor.g << 16u);
        let packed_ba = packedColor.b | (packedColor.a << 16u);
        cachedColor = vec4f(unpack2x16float(packed_rg), unpack2x16float(packed_ba));
    #endif
    return cachedColor.a;
}

fn getColor() -> vec3f {
    return cachedColor.rgb;
}

fn getRotation() -> vec4f {
    let rotXY = unpack2x16float(cachedTransformA.a);
    let rotZscaleX = unpack2x16float(cachedTransformB.x);
    let rotXYZ = vec3f(rotXY, rotZscaleX.x);
    return vec4f(rotXYZ, sqrt(max(0.0, 1.0 - dot(rotXYZ, rotXYZ)))).wxyz;
}

fn getScale() -> vec3f {
    let rotZscaleX = unpack2x16float(cachedTransformB.x);
    let scaleYZ = unpack2x16float(cachedTransformB.y);
    return vec3f(rotZscaleX.y, scaleYZ);
}
`

export const GSPLAT_LARGE_WRITE_GLSL = /* glsl */ `
// Large work buffer format (32 bytes/splat).
void writeSplat(vec3 center, vec4 rotation, vec3 scale, vec4 color) {
    #ifdef GSPLAT_COLOR_UINT
        uint packed_rg = packHalf2x16(color.rg);
        uint packed_ba = packHalf2x16(color.ba);
        writeDataColor(uvec4(
            packed_rg & 0xFFFFu,
            packed_rg >> 16u,
            packed_ba & 0xFFFFu,
            packed_ba >> 16u
        ));
    #else
        writeDataColor(color);
    #endif
    #ifndef GSPLAT_COLOR_ONLY
        writeDataTransformA(uvec4(floatBitsToUint(center.x), floatBitsToUint(center.y), floatBitsToUint(center.z), packHalf2x16(rotation.xy)));
        writeDataTransformB(uvec4(packHalf2x16(vec2(rotation.z, scale.x)), packHalf2x16(scale.yz), 0u, 0u));
    #endif
}
`

export const GSPLAT_LARGE_WRITE_WGSL = /* wgsl */ `
// Large work buffer format (32 bytes/splat).
fn writeSplat(center: vec3f, rotation: vec4f, scale: vec3f, color: vec4f) {
    #ifdef GSPLAT_COLOR_UINT
        let packed_rg = pack2x16float(color.rg);
        let packed_ba = pack2x16float(color.ba);
        writeDataColor(vec4u(
            packed_rg & 0xFFFFu,
            packed_rg >> 16u,
            packed_ba & 0xFFFFu,
            packed_ba >> 16u
        ));
    #else
        writeDataColor(color);
    #endif
    #ifndef GSPLAT_COLOR_ONLY
        writeDataTransformA(vec4u(bitcast<u32>(center.x), bitcast<u32>(center.y), bitcast<u32>(center.z), pack2x16float(rotation.xy)));
        writeDataTransformB(vec4u(pack2x16float(vec2f(rotation.z, scale.x)), pack2x16float(scale.yz), 0u, 0u));
    #endif
}
`
